import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'c30.zoho.browser_confirmed';
const GATE_ENABLED_KEY = 'c30.zoho.gate_enabled';   // '1' = on, anything else = off (default)

export const isZohoGateEnabled = () => {
  try { return localStorage.getItem(GATE_ENABLED_KEY) === '1'; } catch (_) { return false; }
};
export const setZohoGateEnabled = (on) => {
  try {
    if (on) localStorage.setItem(GATE_ENABLED_KEY, '1');
    else localStorage.removeItem(GATE_ENABLED_KEY);
  } catch (_) { /* ignore */ }
};

const ZohoSessionContext = createContext({
  isLinked: false,
  isSuperAdmin: false,
  isCrmManager: false,
  hasConfirmedZoho: false,
  loading: true,
  confirm: () => {},
  revoke: () => {},
  requireConfirmed: () => false,
  promptOpen: false,
  openPrompt: () => {},
  closePrompt: () => {},
});

/**
 * App-wide provider that tracks whether the current user (a) is linked to a
 * Zoho CRM seat and (b) has confirmed — via a self-attestation gate — that
 * they are signed into Zoho CRM in this browser session.
 *
 * Rationale: building per-user OAuth is a big change. In the meantime we
 * simply require the user to tell us "yes I'm signed in" after opening
 * crm.zoho.eu in a new tab. That confirmation persists for the lifetime of
 * the current browser tab/session (sessionStorage) so they see the gate
 * at most once per session. When they close the tab and re-open the C30
 * dashboard later, they'll re-confirm — which lines up with how Zoho's own
 * session cookie expires.
 */
export const ZohoSessionProvider = ({ children }) => {
  const [status, setStatus] = useState({ is_linked: false, is_super_admin: false, is_crm_manager: false, is_sales_team: false, is_admin_group: false });
  const [loading, setLoading] = useState(true);
  const [hasConfirmedZoho, setHasConfirmedZoho] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (_) { return false; }
  });
  const [promptOpen, setPromptOpen] = useState(false);

  // Load status once per mount — `/api/zoho/my-status` is cheap (1 DB read
  // plus a cached Zoho user lookup).
  //
  // Re-fetch is also triggered on a `c30:auth-changed` window event so that
  // logging in (or switching accounts) re-queries the gate flags. Without
  // this, the provider only ran when the app was first mounted — if that
  // happened on the /login page, it fired off an unauthenticated request,
  // got 401, and the fallback all-false status persisted forever, hiding
  // the notification bars even after a successful login.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setLoading(true);
      api.get(`${API}/zoho/my-status`)
        .then(r => { if (!cancelled) setStatus(r.data || {}); })
        .catch(() => { if (!cancelled) setStatus({ is_linked: false, is_super_admin: false, is_crm_manager: false, is_sales_team: false, is_admin_group: false }); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const onAuthChanged = () => load();
    window.addEventListener('c30:auth-changed', onAuthChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('c30:auth-changed', onAuthChanged);
    };
  }, []);

  const confirm = useCallback(() => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* ignore */ }
    setHasConfirmedZoho(true);
    setPromptOpen(false);
  }, []);

  const revoke = useCallback(() => {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (_) { /* ignore */ }
    setHasConfirmedZoho(false);
  }, []);

  const openPrompt = useCallback(() => setPromptOpen(true), []);
  const closePrompt = useCallback(() => setPromptOpen(false), []);

  /**
   * Gate helper — call from any feature entry point. If the user is not
   * linked or not confirmed, it opens the gate prompt and returns false.
   * If they're all good, returns true. Feature UIs should do:
   *   if (!requireConfirmed()) return;
   */
  const requireConfirmed = useCallback(() => {
    // The Zoho browser-session gate is now opt-in via Settings → Integrations.
    // It's OFF by default — any user with backend-side CRM access (Zoho seat,
    // sales team membership, or super-admin) just gets straight through.
    if (!isZohoGateEnabled()) {
      return true;
    }
    if (!status.is_linked) {
      // Not a Zoho user — show the prompt which will surface the admin-help copy.
      setPromptOpen(true);
      return false;
    }
    if (!hasConfirmedZoho) {
      setPromptOpen(true);
      return false;
    }
    return true;
  }, [status.is_linked, hasConfirmedZoho]);

  const value = useMemo(() => ({
    isLinked: !!status.is_linked,
    isSuperAdmin: !!status.is_super_admin,
    isPrimarySuperAdmin: !!status.is_primary_super_admin,
    isCrmManager: !!status.is_crm_manager,
    isSalesTeam: !!status.is_sales_team,
    isAdminGroup: !!status.is_admin_group,
    hasConfirmedZoho,
    loading,
    confirm,
    revoke,
    requireConfirmed,
    promptOpen,
    openPrompt,
    closePrompt,
  }), [status, hasConfirmedZoho, loading, confirm, revoke, requireConfirmed, promptOpen, openPrompt, closePrompt]);

  return (
    <ZohoSessionContext.Provider value={value}>
      {children}
    </ZohoSessionContext.Provider>
  );
};

export const useZohoSession = () => useContext(ZohoSessionContext);

export default ZohoSessionContext;
